/**
 * Round-trip of the `@theme-knobs` header (Spec 340).
 *
 * The generated stylesheet carries its knob values as JSON inside a CSS comment at
 * the very top — a comment whose body is the marker `@theme-knobs` followed by the
 * JSON object, for example:
 *
 *     @theme-knobs {"v":1,"ctaColor":"#45D43B","ctaGradientTo":null,"glow":true,"radius":999}
 *
 * The editor reads this header to re-open a previously generated stylesheet. We only
 * ever READ the header from existing CSS; writing always happens fresh from the
 * template (no CSS parsing on the write path).
 *
 * Pure module — no DOM, no network.
 */
import { type ThemeKnobs, coerceKnobs } from './theme-knobs';

const HEADER_MARKER = '@theme-knobs';

/** Build the `@theme-knobs` comment line for the top of a generated stylesheet. */
export function serializeKnobs(knobs: ThemeKnobs): string {
  // Stable key order keeps generated CSS deterministic across runs.
  const payload = JSON.stringify({
    v: knobs.v,
    ctaColor: knobs.ctaColor,
    ctaGradientTo: knobs.ctaGradientTo,
    glow: knobs.glow,
    radius: knobs.radius,
  });
  return `/* ${HEADER_MARKER} ${payload} */`;
}

/**
 * Read knobs back from a stylesheet's `@theme-knobs` header. Returns null when the
 * header is absent, malformed, or carries invalid values — the caller then falls
 * back to defaults (and, for a non-empty stylesheet without a header, shows the
 * "custom CSS" warning). Never throws.
 */
export function parseKnobsFromCss(css: string): ThemeKnobs | null {
  if (typeof css !== 'string' || css.indexOf(HEADER_MARKER) === -1) return null;

  // Match: /* @theme-knobs { ... } */  — capture the JSON object (non-greedy up to */).
  const match = css.match(/\/\*\s*@theme-knobs\s*(\{[\s\S]*?\})\s*\*\//);
  if (!match) return null;

  try {
    const parsed = JSON.parse(match[1]) as unknown;
    return coerceKnobs(parsed);
  } catch {
    return null;
  }
}

/**
 * Whether a stylesheet was produced by the theme editor (i.e. carries a *valid*
 * `@theme-knobs` header). Used to warn before overwriting hand-crafted custom CSS.
 */
export function hasThemeKnobsHeader(css: string): boolean {
  return parseKnobsFromCss(css) !== null;
}

/**
 * Whether the `@theme-knobs` marker is present at all — regardless of whether the
 * payload is valid. Lets callers distinguish a *malformed* header (marker present,
 * editor-authored → silent default) from genuinely hand-crafted CSS (no marker → warn).
 */
export function hasThemeKnobsMarker(css: string): boolean {
  return typeof css === 'string' && css.indexOf(HEADER_MARKER) !== -1;
}
